import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Badge } from './ui/badge';

const CryptoList = ({ cryptos, selectedCrypto, onSelect }) => {
  const [hoveredId, setHoveredId] = useState(null);

  const formatPrice = (price) => {
    if (price >= 1) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${price.toFixed(6)}`;
  };

  const formatMarketCap = (mc) => {
    if (mc >= 1e12) return `$${(mc / 1e12).toFixed(2)}T`;
    if (mc >= 1e9) return `$${(mc / 1e9).toFixed(2)}B`;
    if (mc >= 1e6) return `$${(mc / 1e6).toFixed(2)}M`;
    return `$${mc.toFixed(0)}`;
  };

  return (
    <Card className="border" style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }} data-testid="crypto-list-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <span style={{ color: '#0F172A' }}>Top Cryptocurrencies</span>
          <Badge variant="secondary" style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
            {cryptos.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[600px]">
          <div className="space-y-1 px-3 py-2" data-testid="crypto-list-items">
            {cryptos.map((crypto, index) => {
              const quote = crypto.quote?.USD;
              if (!quote) return null;
              
              const isSelected = selectedCrypto?.id === crypto.id;
              const isPositive = quote.percent_change_24h >= 0;
              
              return (
                <motion.div
                  key={crypto.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  whileHover={{ scale: 1.02, x: 4 }}
                  onMouseEnter={() => setHoveredId(crypto.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => onSelect(crypto)}
                  className="p-2.5 rounded-lg cursor-pointer transition-all border"
                  style={{
                    backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
                    borderColor: isSelected ? '#1D4ED8' : '#E5E7EB',
                    borderWidth: isSelected ? '2px' : '1px'
                  }}
                  data-testid={`crypto-item-${crypto.symbol}`}
                >
                  <div className="flex items-center gap-2">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                      <motion.div
                        animate={{
                          rotate: hoveredId === crypto.id ? 360 : 0
                        }}
                        transition={{ duration: 0.6 }}
                        className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden"
                        style={{ backgroundColor: '#F8FAFC' }}
                      >
                        <img
                          src={`https://s2.coinmarketcap.com/static/img/coins/64x64/${crypto.id}.png`}
                          alt={crypto.name}
                          className="w-7 h-7"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://cryptologos.cc/logos/${crypto.name.toLowerCase().replace(' ', '-')}-${crypto.symbol.toLowerCase()}-logo.png`;
                          }}
                        />
                      </motion.div>
                    </div>

                    {/* Name and Symbol */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-semibold text-sm truncate" style={{ color: '#0F172A' }} data-testid={`crypto-name-${crypto.symbol}`}>
                          {crypto.name}
                        </span>
                        <Badge variant="outline" className="text-xs font-mono flex-shrink-0 px-1 py-0" style={{ borderColor: '#E5E7EB', color: '#475569' }}>
                          {crypto.symbol}
                        </Badge>
                      </div>
                      <div className="text-xs" style={{ color: '#475569' }} data-testid={`crypto-price-${crypto.symbol}`}>
                        {formatPrice(quote.price)}
                      </div>
                    </div>
                    
                    {/* Price Change - Always Visible */}
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0 min-w-[70px]">
                      <motion.div
                        key={quote.percent_change_24h}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        className="flex items-center justify-end gap-0.5 text-xs font-semibold whitespace-nowrap"
                        style={{ color: isPositive ? '#16A34A' : '#DC2626' }}
                        data-testid={`crypto-change-${crypto.symbol}`}
                      >
                        {isPositive ? (
                          <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" />
                        ) : (
                          <TrendingDown className="w-3.5 h-3.5 flex-shrink-0" />
                        )}
                        <span>{Math.abs(quote.percent_change_24h).toFixed(2)}%</span>
                      </motion.div>
                      <div className="text-[10px] whitespace-nowrap" style={{ color: '#475569' }}>
                        {formatMarketCap(quote.market_cap)}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default CryptoList;
